import { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { NfcCard, Query } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS } from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect, useMemo } from 'react';
import {
  isCardBlockedStatus,
  isCardExpiredStatus,
  isCardExpiresSoonStatus,
  isRightsChangedStatus
} from 'utils/NfcCard/nfcCard';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getNfcCards'>> =
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS;

const useDashboardCards = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: cardsLoading,
    error: cardsError,
    data: cardsData
  } = useQuery(GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (cardsError) {
      handleFetchError(translation.access_card_error_fetching);
      console.error(cardsError);
    }
  }, [cardsError, handleFetchError, translation.access_card_error_fetching]);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingDashboardCards = !!cardsData && cardsLoading;

  useRegisterBackgroundRefetch(isRefetchingDashboardCards);

  const cardCounts = useMemo(() => {
    const cards = (cardsData?.getNfcCards ?? []) as NfcCard[];

    return cards.reduce(
      (acc, card) => {
        if (isCardExpiredStatus(card)) acc.cardsExpired++;
        if (isRightsChangedStatus(card)) acc.cardsRightsChanged++;
        if (isCardBlockedStatus(card)) acc.cardsBlocked++;
        if (isCardExpiresSoonStatus(card)) acc.cardsExpiringSoon++;

        return acc;
      },
      {
        cardsExpired: 0,
        cardsRightsChanged: 0,
        cardsBlocked: 0,
        cardsExpiringSoon: 0
      }
    );
  }, [cardsData]);

  return {
    ...cardCounts,
    totalCards: cardsData?.getNfcCards.length ?? 0,
    loadingDashboardCards: cardsLoading && !cardsData,
    isRefetchingDashboardCards
  };
};

export default useDashboardCards;
